import {useMemo} from 'react'

import {parseFilterKeywords} from './utils/parse-filter-keywords'
import {filterConversationItems, filterThreads, type ParsedCommentsFilterState} from './utils/filter-items'
import {buildConversationItems, type ConversationItem} from './utils/build-conversation-items'

import {useDiffPathOrderMap, usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate, sortThreadItemsByDiffOrder} from './utils/sort-items'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommentsSort} from './use-comments-sort-state'
import type {CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import type {CommentsDisplayMode} from './utils/display-mode'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Current sort direction. */
  sort: CommentsSort
  displayMode: CommentsDisplayMode
}

export interface FilteredCommentsData {
  /** Unique authors of unfiltered items, for the author filter. */
  authorList: AuthorFilterItem[]
  /** Conversation items matching the conversation filter and sort direction. */
  filteredConversationItems: ConversationItem[]
  /** Total number of conversation items in this PR. */
  totalConversationItemsCount: number
}

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
  authorItems: [],
}

export function useFilteredCommentsData({
  filterState,
  sort,
  displayMode,
}: UseFilteredCommentsDataOptions): FilteredCommentsData {
  const parsedFilterState = useMemo<ParsedCommentsFilterState>(
    () => ({
      ...filterState,
      parsedKeywords: parseFilterKeywords(filterState.filterText),
    }),
    [filterState],
  )

  const threadPreviews = useThreads()

  const diffPathOrderMap = useDiffPathOrderMap()

  const basePath = usePullRequestBasePath()

  const conversationComments = useInfiniteConversationCommentsQuery({
    basePath,
    enabled: displayMode === 'conversation',
  }).data?.comments

  // Review thread mode displays threads as flat list without issue comments or review summaries
  const reviewThreadItems = useMemo(() => {
    if (displayMode !== 'review-threads') return emptyItems

    const unfilteredItems = buildConversationItems([], threadPreviews)
    const filteredItems = filterConversationItems(unfilteredItems, parsedFilterState)
    const filteredSortedItems =
      sort === 'diff-order'
        ? sortThreadItemsByDiffOrder(filteredItems, diffPathOrderMap)
        : sortConversationItemsByDate(filteredItems, sort)

    return {unfilteredItems, filteredSortedItems, authorItems: unfilteredItems}
  }, [parsedFilterState, threadPreviews, diffPathOrderMap, sort, displayMode])

  // Conversation mode displays full list of comments and reviews with threads
  const conversationItems = useMemo(() => {
    // Diff-order sort is not supported for conversation mode
    if (displayMode !== 'conversation' || sort === 'diff-order') return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = isFeatureEnabled('prs_docked_comments_replies')
      ? filterThreads(threadPreviews, parsedFilterState)
      : []
    const authorCountFilterState = {
      ...parsedFilterState,
      selectedAuthors: undefined,
    }
    const authorThreads = isFeatureEnabled('prs_docked_comments_replies')
      ? filterThreads(threadPreviews, authorCountFilterState)
      : []

    const unfilteredItems = buildConversationItems(
      conversationComments ?? [],
      isFeatureEnabled('prs_docked_comments_replies') ? threadPreviews : [],
    )
    const filterableItems = buildConversationItems(conversationComments ?? [], threadsForItems)
    const authorItems = buildConversationItems(conversationComments ?? [], authorThreads)
    const filteredSortedItems = sortConversationItemsByDate(
      filterConversationItems(filterableItems, parsedFilterState),
      sort,
    )

    return {unfilteredItems, filteredSortedItems, authorItems}
  }, [displayMode, threadPreviews, parsedFilterState, conversationComments, sort])

  // Even though we resolve to a single value, the two separate memos help minimize dependencies per-mode to reduce
  // unnecessary calculations and renders, at a small complexity cost
  const resolvedItems = displayMode === 'review-threads' ? reviewThreadItems : conversationItems

  const authorList = useMemo(() => {
    const authorCountFilterState = {
      ...parsedFilterState,
      selectedAuthors: undefined,
    }
    const filteredItemsForAuthorCounts = filterConversationItems(resolvedItems.authorItems, authorCountFilterState)
    return getConversationItemsAuthors(resolvedItems.authorItems, filteredItemsForAuthorCounts)
  }, [parsedFilterState, resolvedItems.authorItems])

  return {
    filteredConversationItems: resolvedItems.filteredSortedItems,
    authorList,
    totalConversationItemsCount: resolvedItems.unfilteredItems?.length ?? 0,
  }
}
